package com.corbettcode.allaboutrocks.pages

import androidx.compose.runtime.Composable
import com.corbettcode.allaboutrocks.HeadlineTextStyle
import com.corbettcode.allaboutrocks.HeroContainerStyle
import com.corbettcode.allaboutrocks.PageHeadlineStyle
import com.corbettcode.allaboutrocks.PageSectionTitleStyle
import com.corbettcode.allaboutrocks.SectionTextStyle
import com.corbettcode.allaboutrocks.components.layouts.PageLayoutData
import com.corbettcode.allaboutrocks.components.widgets.CarouselOfSections
import com.corbettcode.allaboutrocks.toSitePalette
import com.corbettcode.allaboutrocks.utility.textColor
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment.CenterHorizontally
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.data.add
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.layout.HorizontalDivider
import com.varabyte.kobweb.silk.components.layout.SimpleGrid
import com.varabyte.kobweb.silk.components.layout.numColumns
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toAttrs
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Text
import kotlin.time.Duration.Companion.seconds

// --- Constants ---
private val HERO_IMAGE_MAX_WIDTH = 400.px

private val PRESS_IMAGE_MAX_HEIGHT = 800.px
private val PRESS_IMAGE_MAX_WIDTH = 600.px
private val PRESS_IMAGE_SMALLER_MAX_WIDTH = 600.px
private val PRESS_IMAGE_VERTICAL_MARGIN = 24.px

private val PROGRAM_POSTER_IMAGE_MAX_HEIGHT = 800.px
private val PROGRAM_POSTER_IMAGE_MAX_WIDTH = 400.px
private val PROGRAM_POSTER_IMAGE_VERTICAL_MARGIN = 24.px

// Breathing room above a section when the nav header deep-links to it.
private val SECTION_SCROLL_MARGIN = 2.cssRem

@InitRoute
fun initLearnIdentifyRocks(ctx: InitRouteContext) {
    ctx.data.add(PageLayoutData("Lean How to Identify Rocks"))
}

@Composable
private fun LearnIdentifyRocksHeadline() {
    val sitePalette = ColorMode.current.toSitePalette()
//    val textColor = if (ColorMode.current == ColorMode.LIGHT) Colors.Black else Colors.White

    Div(HeadlineTextStyle.toAttrs()) {
        Column {
            SpanText(
                "Learn How to Identify Rocks"
                , PageHeadlineStyle.toModifier()
                    .color(textColor())
                    .align(alignment = CenterHorizontally)
            )
//            SpanText(
//                "Junior Rockhounds",
//                PageHeadlineStyle.toModifier()
//                    .color(sitePalette.brand.accent)
//                    .textShadow(0.px, 0.px, blurRadius = 0.5.cssRem, color = Colors.Gray)
//            )
        }
    }
}

@Composable
private fun LearnIdentifyRocksHero() {
    LearnIdentifyRocksHeadline()

    // 1 column on mobile, 2 columns on md+ (text left, image right)
    SimpleGrid(
        numColumns = numColumns(1, md = 2),
        modifier = HeroContainerStyle.toModifier().fillMaxWidth().gap(2.cssRem)
    ) {
        Box {
            Column(Modifier.gap(2.cssRem)) {

                Image(
                    src = "/images/learn/identify_rocks/how_to_identify_rocks_chart.jpg",
                    alt = "how to identify rocks",
                    modifier = Modifier
                        .maxWidth(HERO_IMAGE_MAX_WIDTH)
                        .fillMaxWidth()  // shrinks naturally on small screens
                )
            }
        }
        Image(
            src = "/images/learn/identify_rocks/easy_jasper_identification.jpg",
            alt = "easy jasper identification",
            modifier = Modifier
                .maxWidth(HERO_IMAGE_MAX_WIDTH)
                .fillMaxWidth()  // shrinks naturally on small screens
        )
    }
}

/**
 * Wraps a section in an anchor target, so the "Identify Rocks" submenu in the nav header can deep-link
 * straight to it (e.g. "/learn_identify_rocks#jasper").
 *
 * @param anchorId The id of the anchor target.
 * @param content The content to wrap in the anchor target.
 */
@Composable
private fun AnchorSection(
    anchorId: String
    , content: @Composable () -> Unit
) {
    Div(
        attrs = Modifier
            .fillMaxWidth()
            // Keep the section clear of the top of the viewport once scrolled to.
            .scrollMarginBlock(start = SECTION_SCROLL_MARGIN)
            .toAttrs { id(anchorId) }
    ) {
        content()
    }
}

@Composable
private fun LearnIdentifyRocksPosterSection(
    title: String
    , imageSrc: String
    , imageAlt: String
) {
//    val textColor = if (ColorMode.current == ColorMode.LIGHT) Colors.Black else Colors.White

    Div(SectionTextStyle.toAttrs()) {
        Column {
            SpanText(
                title
                , PageSectionTitleStyle.toModifier()
                    .color(textColor())
                    .align(alignment = CenterHorizontally)
                    .margin(topBottom = 2.cssRem)
            )

            Image(
                src = imageSrc
                , alt = imageAlt
                , modifier = Modifier
                    .maxWidth(PRESS_IMAGE_MAX_WIDTH)
                    .maxHeight(PRESS_IMAGE_MAX_HEIGHT)
                    .fillMaxWidth()
                    .align(alignment = CenterHorizontally)
            )
        }
    }
}

@Composable
private fun LearnIdentifyRocksPressSection() {
    // Single centered column — image scales with viewport on small screens
    SimpleGrid(
        numColumns = numColumns(1)
        , modifier = Modifier.fillMaxWidth()
    ) {
        Image(
            src = "/images/typesof/petrifiedwood/petrified_wood.jpg"
            , alt = "petrified_wood"
            , modifier = Modifier
                .maxWidth(PRESS_IMAGE_MAX_WIDTH)
                .maxHeight(PRESS_IMAGE_MAX_HEIGHT)
                .fillMaxWidth()  // shrinks on small screens, caps at max on large
                .margin(top = PRESS_IMAGE_VERTICAL_MARGIN, bottom = PRESS_IMAGE_VERTICAL_MARGIN)
        )
//
//        Image(
//            src = "/images/learn/books/Rocks_and_Minerals_of_the_Pacific_Coast.jpg",
//            alt = "petrified_wood",
//            modifier = Modifier
//                .maxWidth(400.px)
//                .maxHeight(900.px)
//                .fillMaxWidth()  // shrinks on small screens, caps at max on large
//                .margin(top = PRESS_IMAGE_VERTICAL_MARGIN, bottom = PRESS_IMAGE_VERTICAL_MARGIN)
//        )
//
    }
}

@Composable
private fun LearnIdentifyRocksBookSection() {
    val sitePalette = ColorMode.current.toSitePalette()
//    val textColor = if (ColorMode.current == ColorMode.LIGHT) Colors.Black else Colors.White

    Div(SectionTextStyle.toAttrs()) {
        Column {
            SpanText(
                "Books on how to Identify Rocks"
                , PageSectionTitleStyle.toModifier()
                    .color(textColor())
                    .align(alignment = CenterHorizontally)
                    .margin(topBottom = 2.cssRem)
            )
        }

        CarouselOfSections(
            items = learnIdentifyRocksCarouselSlides
            , frameDuration = 8.seconds
        )
    }
}

@Page("learn_identify_rocks")
@Layout(".components.layouts.PageLayout")
@Composable
fun LearnIdentifyRocksPage() {
    LearnIdentifyRocksHero()
    HorizontalDivider(Modifier
        .fillMaxWidth()
        .margin(topBottom = 2.cssRem)
    )
    AnchorSection("petrified-wood") {
        LearnIdentifyRocksPressSection()
    }
    HorizontalDivider(Modifier
        .fillMaxWidth()
        .margin(topBottom = 2.cssRem)
    )
    AnchorSection("books") {
        LearnIdentifyRocksBookSection()
    }
}

val learnIdentifyRocksCarouselSlides = listOf<@Composable () -> Unit>(
    {
        SimpleGrid(
            numColumns = numColumns(2)
            , modifier = Modifier.fillMaxWidth()
        ) {
            Image(
                src = "/images/learn/books/Rocks_and_Minerals_of_the_Pacific_Coast.jpg",
                alt = "how to tumble rocks",
                modifier = Modifier
                    .maxWidth(HERO_IMAGE_MAX_WIDTH)
                    .fillMaxWidth()  // shrinks naturally on small screens
            )

            P {
                Text("Get the perfect guide to rocks and minerals of the Pacific Coast Keep this tabbed booklet close at hand on your next rock-hunting adventure. Based on Dan R. Lynch and Bob Lynch's best-selling field guides and featuring 52 of the most common and desirable rocks and minerals along the coasts of California, Oregon, and Washington, the booklet is organized by rocks/minerals and then by general appearance for quick and easy identification. Narrow your choices by appearance, and view photos of just a few specimens at a time. The easy-to-use format means you'll quickly find what you need to know. Plus, the quick guide is much easier to use than laminated foldouts, and the tear-resistant pages help to make the book durable in the field.")

            }
        }
    }
    , {
        SimpleGrid(
            numColumns = numColumns(2)
            , modifier = Modifier.fillMaxWidth()
        ) {
            Image(
                src = "/images/learn/books/washington_oregon_rocks_and_minerals.jpg",
                alt = "how to tumble rocks",
                modifier = Modifier
                    .maxWidth(HERO_IMAGE_MAX_WIDTH)
                    .fillMaxWidth()  // shrinks naturally on small screens
            )

            P {
                Text("Identify and collect rocks and minerals with the perfect guide to the Pacific Northwest! With this famous field guide by Dan R. Lynch and Bob Lynch, field identification is simple and informative. The book features comprehensive entries for 124 rocks and minerals, from common rocks to rare finds. That means you’re more likely to identify what you’ve found. The authors know rocks and took their own full-color photographs to depict the detail needed for identification―no more guessing from line drawings. The field guide’s easy-to-use format helps you to quickly find what you need to know and where to look.")

            }
        }
    }
    , {
        SimpleGrid(
            numColumns = numColumns(2)
            , modifier = Modifier.fillMaxWidth()
        ) {
            Image(
                src = "/images/learn/books/agates_and_other_collectibles_of_the_pacific_coast.jpg",
                alt = "how to tumble rocks",
                modifier = Modifier
                    .maxWidth(HERO_IMAGE_MAX_WIDTH)
                    .fillMaxWidth()  // shrinks naturally on small screens
            )

            P {
                Text("Keep this tabbed booklet close at hand on your next rock-hunting adventure. Based on Dan R. Lynch’s best-selling field guides and featuring the most common and desirable agates and other collectibles along the Pacific Coast, the booklet is organized by rocks/minerals and then by general appearance for quick and easy identification. With six varieties of agates, as well as popular finds like Jasper, Quartz, and even Jade, this handy guide has the information you want. Narrow your choices by appearance, and view photos of just a few specimens at a time. The easy-to-use format means you’ll quickly find what you need to know. Plus, the quick guide is much easier to use than laminated foldouts, and the tear-resistant pages help to make the book durable in the field.")

            }
        }
    }
)
